/* eslint-disable max-lines, react/jsx-max-depth */
import { type ReactNode, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  AdminTenderCaseSummary,
  AdminTenderDerivedStatus,
  AdminTenderRunSummary,
  AdminTenderUsageSummary,
  AdminTenderUsageWindow,
} from "@/api/admin-api"
import type { TFunction } from "i18next"

import { downloadAdminTenderTraceCsv, getAdminTenderTraceJson } from "@/api/admin-api"
import { ExternalLink, FileSearch, RefreshCw } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  useAdminTenderCaseDetailQuery,
  useAdminTenderCasesQuery,
  useAdminTenderRunsQuery,
  useAdminTenderUsageSummaryQuery,
} from "@/features/admin-dashboard/hooks/admin-queries"

type ViewMode = "runs" | "cases"

type StatusFilter = "all" | AdminTenderDerivedStatus

function buildTraceFilename(runNumber: number | null | undefined, step: string, extension: string) {
  const runSegment = typeof runNumber === "number" ? `run-${runNumber}` : "run"
  return `tender-${runSegment}-${step}-trace.${extension}`
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, 100)
}

function formatDateTime(value: string | null | undefined, locale: string) {
  if (!value) {
    return "—"
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed)
}

/**
 * Token and cost breakdown for one case.
 *
 * `usage` is null when the lookup failed, which has to read differently from a
 * case that genuinely spent nothing — showing "0 SEK" for a failed query would
 * be a confident lie about money.
 */
function TenderUsageContent({ usage }: { usage: AdminTenderUsageSummary | null }) {
  const { t } = useTranslation()

  if (!usage) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.tenders.usage.unavailable")}
      </p>
    )
  }

  if (usage.llmCalls === 0) {
    return (
      <p className="text-muted-foreground text-sm">{t("adminDashboard.tenders.usage.noUsage")}</p>
    )
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usage.estimatedCost")}
          </div>
          <div className="mt-2 text-2xl font-semibold">
            {/* Nothing priced at all: showing "0.00" would be a fabricated
                figure, not a cheap case. */}
            {usage.costComplete || usage.cost > 0
              ? formatCost(usage.cost, usage.currency)
              : t("adminDashboard.tenders.usage.costUnavailable")}
          </div>
          {!usage.costComplete && (
            <div className="text-destructive mt-1 text-xs">
              {t("adminDashboard.tenders.usage.incompleteEstimate", {
                models: usage.unpricedModels.join(", "),
              })}
            </div>
          )}
          {usage.costComplete && usage.pricingSource === "manual" && (
            <div className="text-muted-foreground mt-1 text-xs">
              {t("adminDashboard.tenders.usage.fallbackPricing")}
            </div>
          )}
        </div>
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usage.totalTokens")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{formatTokens(usage.totalTokens)}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {t("adminDashboard.tenders.usage.tokenSplit", {
              input: formatTokens(usage.inputTokens),
              output: formatTokens(usage.outputTokens),
            })}
          </div>
        </div>
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usage.cachedInput")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{cachedShare(usage)}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {formatTokens(usage.cachedInputTokens)}
          </div>
        </div>
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usage.llmCalls")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{usage.llmCalls.toLocaleString()}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {t("adminDashboard.tenders.usage.stepExecutions", {
              count: usage.stepExecutions,
              runs: usage.runCount,
            })}
          </div>
        </div>
      </div>

      {usage.failedStepExecutions > 0 && (
        <p className="text-muted-foreground text-sm">
          {t("adminDashboard.tenders.usage.includesFailed", { count: usage.failedStepExecutions })}
        </p>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("adminDashboard.tenders.usage.model")}</TableHead>
            <TableHead>{t("adminDashboard.tenders.usage.calls")}</TableHead>
            <TableHead>{t("adminDashboard.tenders.usage.input")}</TableHead>
            <TableHead>{t("adminDashboard.tenders.usage.output")}</TableHead>
            <TableHead>{t("adminDashboard.tenders.usage.cost")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {usage.byModel.map((row) => (
            <TableRow key={row.model}>
              <TableCell className="font-medium">{row.model}</TableCell>
              <TableCell>{row.llmCalls.toLocaleString()}</TableCell>
              <TableCell>{formatTokens(row.inputTokens)}</TableCell>
              <TableCell>{formatTokens(row.outputTokens)}</TableCell>
              <TableCell>{formatCost(row.cost, usage.currency)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Per-run cost is what makes an expensively re-run case visible; the
          model table alone flattens every run together. */}
      {usage.byRun.length > 1 && (
        <div className="space-y-2">
          <div className="text-muted-foreground text-sm font-medium">
            {t("adminDashboard.tenders.usage.byRun")}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminDashboard.tenders.usage.run")}</TableHead>
                <TableHead>{t("adminDashboard.tenders.usage.calls")}</TableHead>
                <TableHead>{t("adminDashboard.tenders.usage.totalTokens")}</TableHead>
                <TableHead>{t("adminDashboard.tenders.usage.cost")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usage.byRun.map((row) => (
                <TableRow key={row.runId || "unknown"}>
                  <TableCell className="font-mono text-xs">
                    {row.runId || t("adminDashboard.tenders.usage.unknownRun")}
                  </TableCell>
                  <TableCell>{row.llmCalls.toLocaleString()}</TableCell>
                  <TableCell>{formatTokens(row.totalTokens)}</TableCell>
                  <TableCell>{formatCost(row.cost, usage.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {usage.byStep.length > 0 && (
        <div className="space-y-2">
          <div className="text-muted-foreground text-sm font-medium">
            {t("adminDashboard.tenders.usage.bySelectedRunStep")}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminDashboard.tenders.usage.step")}</TableHead>
                <TableHead>{t("adminDashboard.tenders.usage.calls")}</TableHead>
                <TableHead>{t("adminDashboard.tenders.usage.input")}</TableHead>
                <TableHead>{t("adminDashboard.tenders.usage.output")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usage.byStep.map((row) => (
                <TableRow key={row.step}>
                  <TableCell className="font-medium">{stepLabel(row.step, t)}</TableCell>
                  <TableCell>{row.llmCalls.toLocaleString()}</TableCell>
                  <TableCell>{formatTokens(row.inputTokens)}</TableCell>
                  <TableCell>{formatTokens(row.outputTokens)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}

/**
 * Headline spend across all cases in the recent window, above the table.
 *
 * Average and median sit side by side on purpose: per-case cost is
 * right-skewed (measured across 646 real cases the mean ran ~25% above the
 * median, with two thirds of cases below the mean), so an average shown on its
 * own reads as the typical case while overstating it.
 */
function TenderUsageWindowStats({
  query,
}: {
  query: { data?: AdminTenderUsageWindow; isLoading: boolean; isError: boolean }
}) {
  const { t } = useTranslation()

  if (query.isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.tenders.usageWindow.unavailable")}
      </p>
    )
  }

  const stats = query.data
  if (stats.caseCount === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.tenders.usageWindow.noCases", { days: stats.windowDays })}
      </p>
    )
  }

  // Nothing priced at all: "0.00" would be a fabricated figure, not a cheap
  // window. Same rule as the per-case card.
  const costKnown = stats.costComplete || stats.totalCost > 0
  const windowCost = (value: number) =>
    costKnown
      ? formatCost(value, stats.currency)
      : t("adminDashboard.tenders.usage.costUnavailable")

  return (
    <div className="space-y-2">
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usageWindow.averagePerCase")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{windowCost(stats.averageCostPerCase)}</div>
        </div>
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usageWindow.medianPerCase")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{windowCost(stats.medianCostPerCase)}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {t("adminDashboard.tenders.usageWindow.medianHint")}
          </div>
        </div>
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usageWindow.total")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{windowCost(stats.totalCost)}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {formatTokens(stats.totalTokens)}
          </div>
        </div>
        <div className="rounded-xl border p-4">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.tenders.usageWindow.cases")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{stats.caseCount.toLocaleString()}</div>
          <div className="text-muted-foreground mt-1 text-xs">
            {t("adminDashboard.tenders.usageWindow.window", { days: stats.windowDays })}
          </div>
        </div>
      </div>
      {!stats.costComplete && (
        <p className="text-destructive text-xs">
          {t("adminDashboard.tenders.usageWindow.incomplete", {
            models: stats.unpricedModels.join(", "),
          })}
        </p>
      )}
      {stats.costComplete && stats.pricingSource === "manual" && (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.tenders.usage.fallbackPricing")}
        </p>
      )}
    </div>
  )
}

/** Map a pipeline step id onto a localized label — a closed set of three. */
function stepLabel(step: string, t: TFunction): string {
  const known: Record<string, string> = {
    extraction: "adminDashboard.tenders.usage.steps.extraction",
    bid_review: "adminDashboard.tenders.usage.steps.bidReview",
    bid_review_retry: "adminDashboard.tenders.usage.steps.bidReviewRetry",
  }
  // Fall back to the raw id rather than hiding a step we don't have a label for.
  return known[step] ? t(known[step]) : step
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}k`
  return tokens.toLocaleString()
}

function formatCost(cost: number, currency: string): string {
  // Sub-öre costs would render as "0.00" and read as free, so keep enough
  // precision to show that something was spent.
  const decimals = cost > 0 && cost < 0.01 ? 4 : 2
  return `${cost.toFixed(decimals)} ${currency}`
}

function cachedShare(usage: { cachedInputTokens: number; inputTokens: number }): string {
  if (usage.inputTokens === 0) return "—"
  return `${((usage.cachedInputTokens / usage.inputTokens) * 100).toFixed(1)}%`
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds}s`
}

function durationColorClass(ms: number): string {
  if (ms > 120_000) return "text-destructive"
  if (ms > 30_000) return "text-warning-ink "
  return "text-success"
}

function pipelineBadgeVariant(status: string | null | undefined) {
  switch (status) {
    case "completed":
      return "success"
    case "processing":
      return "warning"
    case "failed":
      return "error"
    default:
      return "outline"
  }
}

function derivedBadgeVariant(status: AdminTenderDerivedStatus) {
  switch (status) {
    case "completed":
      return "success"
    case "processing":
      return "warning"
    case "failed":
      return "error"
    default:
      return "outline"
  }
}

function PipelineBadge({ label, status }: { label: string; status: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <Badge variant={pipelineBadgeVariant(status)}>{status ?? "—"}</Badge>
    </div>
  )
}

function StatusFilterSelect({
  value,
  onChange,
}: {
  value: StatusFilter
  onChange: (value: StatusFilter) => void
}) {
  const { t } = useTranslation()
  return (
    <Select value={value} onValueChange={(next) => onChange(next as StatusFilter)}>
      <SelectTrigger className="w-44 rounded-xl">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("adminDashboard.tenders.filters.all")}</SelectItem>
        <SelectItem value="processing">{t("adminDashboard.tenders.filters.processing")}</SelectItem>
        <SelectItem value="failed">{t("adminDashboard.tenders.filters.failed")}</SelectItem>
        <SelectItem value="completed">{t("adminDashboard.tenders.filters.completed")}</SelectItem>
        <SelectItem value="other">{t("adminDashboard.tenders.filters.other")}</SelectItem>
      </SelectContent>
    </Select>
  )
}

function RunRows({
  items,
  locale,
  onSelect,
}: {
  items: AdminTenderRunSummary[]
  locale: string
  onSelect: (row: AdminTenderRunSummary) => void
}) {
  const { t } = useTranslation()
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("adminDashboard.tenders.headers.case")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.run")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.created")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.extraction")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.bidReview")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.cvMatch")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.status")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.runId} className="cursor-pointer" onClick={() => onSelect(item)}>
            <TableCell className="font-medium">{item.caseTitle}</TableCell>
            <TableCell>#{item.runNumber}</TableCell>
            <TableCell>{formatDateTime(item.createdAt, locale)}</TableCell>
            <TableCell>
              <Badge variant={pipelineBadgeVariant(item.extractionStatus)}>
                {item.extractionStatus ?? "—"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={pipelineBadgeVariant(item.bidReviewStatus)}>
                {item.bidReviewStatus ?? "—"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={pipelineBadgeVariant(item.cvMatchStatus)}>
                {item.cvMatchStatus ?? "—"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={derivedBadgeVariant(item.derivedStatus)}>{item.derivedStatus}</Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function CaseRows({
  items,
  locale,
  onSelect,
}: {
  items: AdminTenderCaseSummary[]
  locale: string
  onSelect: (row: AdminTenderCaseSummary) => void
}) {
  const { t } = useTranslation()
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("adminDashboard.tenders.headers.case")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.latestRun")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.runCount")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.extraction")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.bidReview")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.cvMatch")}</TableHead>
          <TableHead>{t("adminDashboard.tenders.headers.activity")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.caseId} className="cursor-pointer" onClick={() => onSelect(item)}>
            <TableCell className="font-medium">{item.caseTitle}</TableCell>
            <TableCell>{item.latestRunNumber ? `#${item.latestRunNumber}` : "—"}</TableCell>
            <TableCell>{item.runCount}</TableCell>
            <TableCell>
              <Badge variant={pipelineBadgeVariant(item.extractionStatus)}>
                {item.extractionStatus ?? "—"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={pipelineBadgeVariant(item.bidReviewStatus)}>
                {item.bidReviewStatus ?? "—"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={pipelineBadgeVariant(item.cvMatchStatus)}>
                {item.cvMatchStatus ?? "—"}
              </Badge>
            </TableCell>
            <TableCell>{formatDateTime(item.latestActivityAt, locale)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function TenderMonitoringSection() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<ViewMode>("runs")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [activeTraceAction, setActiveTraceAction] = useState<string | null>(null)

  const runsQuery = useAdminTenderRunsQuery(statusFilter, 100, viewMode === "runs")
  const casesQuery = useAdminTenderCasesQuery(statusFilter, 50, viewMode === "cases")
  const detailQuery = useAdminTenderCaseDetailQuery(selectedCaseId, selectedRunId, {
    enabled: isSheetOpen,
  })
  const usageSummaryQuery = useAdminTenderUsageSummaryQuery(true)

  const activeItems =
    viewMode === "runs" ? (runsQuery.data?.items ?? []) : (casesQuery.data?.items ?? [])
  const isLoading = viewMode === "runs" ? runsQuery.isLoading : casesQuery.isLoading
  const isRefreshing =
    runsQuery.isFetching || casesQuery.isFetching || (isSheetOpen && detailQuery.isFetching)

  const phaseMetrics = useMemo(
    () =>
      (detailQuery.data?.extraction.phaseMetrics ?? []) as Array<{
        phase?: string
        durationMs?: number
        outcome?: string
        itemCount?: number
        documentCount?: number
      }>,
    [detailQuery.data?.extraction.phaseMetrics]
  )

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "tenders"] })
    if (isSheetOpen && selectedCaseId) {
      await detailQuery.refetch()
    }
  }

  const handleOpenTrace = async (jsonUrl: string, step: string) => {
    const actionKey = `${step}:json`
    setActiveTraceAction(actionKey)

    try {
      const tracePayload = await getAdminTenderTraceJson(jsonUrl)
      const blob = new Blob([JSON.stringify(tracePayload, null, 2)], {
        type: "application/json",
      })
      const url = URL.createObjectURL(blob)
      const openedWindow = window.open(url, "_blank", "noopener,noreferrer")

      if (!openedWindow) {
        triggerBlobDownload(
          blob,
          buildTraceFilename(detailQuery.data?.selectedRun.runNumber, step, "json")
        )
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (error) {
      toast.error(t("adminDashboard.tenders.detail.traceOpenError"), {
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setActiveTraceAction((current) => (current === actionKey ? null : current))
    }
  }

  const handleDownloadTraceCsv = async (csvUrl: string, step: string) => {
    const actionKey = `${step}:csv`
    setActiveTraceAction(actionKey)

    try {
      const blob = await downloadAdminTenderTraceCsv(csvUrl)
      triggerBlobDownload(
        blob,
        buildTraceFilename(detailQuery.data?.selectedRun.runNumber, step, "csv")
      )
    } catch (error) {
      toast.error(t("adminDashboard.tenders.detail.traceDownloadError"), {
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setActiveTraceAction((current) => (current === actionKey ? null : current))
    }
  }

  let mainTableContent: ReactNode
  if (isLoading) {
    mainTableContent = (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    )
  } else if (activeItems.length === 0) {
    mainTableContent = (
      <EmptyState
        description={t("adminDashboard.tenders.empty.description")}
        icon={FileSearch}
        title={t("adminDashboard.tenders.empty.title")}
      />
    )
  } else if (viewMode === "runs") {
    mainTableContent = (
      <RunRows
        items={runsQuery.data?.items ?? []}
        locale={i18n.language}
        onSelect={(row) => {
          setSelectedCaseId(row.caseId)
          setSelectedRunId(row.runId)
          setIsSheetOpen(true)
        }}
      />
    )
  } else {
    mainTableContent = (
      <CaseRows
        items={casesQuery.data?.items ?? []}
        locale={i18n.language}
        onSelect={(row) => {
          setSelectedCaseId(row.caseId)
          setSelectedRunId(row.latestRunId)
          setIsSheetOpen(true)
        }}
      />
    )
  }

  const sheetDescription = detailQuery.data
    ? t("adminDashboard.tenders.detail.subtitle")
    : t("adminDashboard.tenders.detail.loading")

  let sheetBody: ReactNode
  if (!detailQuery.data) {
    sheetBody = (
      <div className="mt-6 space-y-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-16 w-full" />
        ))}
      </div>
    )
  } else {
    sheetBody = (
      <div className="mt-6 space-y-6">
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t("adminDashboard.tenders.detail.selectedRun")}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="text-muted-foreground text-sm">
                {t("adminDashboard.tenders.headers.run")}
              </div>
              <div className="text-lg font-semibold">#{detailQuery.data.selectedRun.runNumber}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-sm">
                {t("adminDashboard.tenders.headers.created")}
              </div>
              <div>{formatDateTime(detailQuery.data.selectedRun.createdAt, i18n.language)}</div>
            </div>
            <PipelineBadge
              label={t("adminDashboard.tenders.headers.extraction")}
              status={detailQuery.data.selectedRun.extractionStatus}
            />
            <PipelineBadge
              label={t("adminDashboard.tenders.headers.bidReview")}
              status={detailQuery.data.selectedRun.bidReviewStatus}
            />
            <PipelineBadge
              label={t("adminDashboard.tenders.headers.cvMatch")}
              status={detailQuery.data.selectedRun.cvMatchStatus}
            />
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs">
                {t("adminDashboard.tenders.headers.status")}
              </span>
              <Badge variant={derivedBadgeVariant(detailQuery.data.selectedRun.derivedStatus)}>
                {detailQuery.data.selectedRun.derivedStatus}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t("adminDashboard.tenders.detail.extraction")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-xl border p-4">
                <div className="text-muted-foreground text-sm">
                  {t("adminDashboard.tenders.detail.pipelineStatus")}
                </div>
                <div className="mt-2">
                  <Badge variant={pipelineBadgeVariant(detailQuery.data.extraction.status)}>
                    {detailQuery.data.extraction.status ?? "—"}
                  </Badge>
                </div>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-muted-foreground text-sm">
                  {t("adminDashboard.tenders.detail.failureReason")}
                </div>
                <div className="mt-2 text-sm">
                  {detailQuery.data.extraction.failureReason ?? "—"}
                </div>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-muted-foreground text-sm">Pipeline Version</div>
                <div className="mt-2">
                  {detailQuery.data.extraction.pipelineVersion ? (
                    <Badge variant="outline">{detailQuery.data.extraction.pipelineVersion}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("adminDashboard.tenders.metrics.phase")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.metrics.duration")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.metrics.outcome")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.metrics.documents")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.metrics.items")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phaseMetrics.map((metric, index) => {
                  const isTotalRow = metric.phase === "pipeline_total"
                  return (
                    <TableRow
                      key={`${metric.phase ?? "phase"}-${index}`}
                      className={isTotalRow ? "border-t-2 font-semibold" : ""}
                    >
                      <TableCell className="font-medium">{metric.phase ?? "—"}</TableCell>
                      <TableCell>
                        {typeof metric.durationMs === "number" ? (
                          <span className={durationColorClass(metric.durationMs)}>
                            {formatDuration(metric.durationMs)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{metric.outcome ?? "—"}</TableCell>
                      <TableCell>
                        {typeof metric.documentCount === "number" ? metric.documentCount : "—"}
                      </TableCell>
                      <TableCell>
                        {typeof metric.itemCount === "number" ? metric.itemCount : "—"}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t("adminDashboard.tenders.usage.title")}</CardTitle>
            {/* Only describe the scope when there is a figure to scope. With no
                usage the list is empty and the line reads "for: ." above a
                message saying the data could not be loaded. */}
            {detailQuery.data.usage && (
              <CardDescription>
                {t("adminDashboard.tenders.usage.scope", {
                  steps: detailQuery.data.usage.covers.map((step) => stepLabel(step, t)).join(", "),
                })}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <TenderUsageContent usage={detailQuery.data.usage} />
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t("adminDashboard.tenders.detail.traceLinks")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {detailQuery.data.traceLinks.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("adminDashboard.tenders.detail.noTraceLinks")}
              </p>
            ) : (
              detailQuery.data.traceLinks.map((trace) => (
                <div key={trace.step} className="flex gap-2">
                  <Button
                    disabled={activeTraceAction !== null}
                    size="sm"
                    variant="outline"
                    onClick={() => void handleOpenTrace(trace.jsonUrl, trace.step)}
                  >
                    <ExternalLink className="mr-2 size-4" />
                    {t("adminDashboard.tenders.detail.openTrace")} {trace.step}
                  </Button>
                  <Button
                    disabled={activeTraceAction !== null}
                    size="sm"
                    variant="outline"
                    onClick={() => void handleDownloadTraceCsv(trace.csvUrl, trace.step)}
                  >
                    {t("adminDashboard.tenders.detail.downloadCsv")}
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t("adminDashboard.tenders.detail.runHistory")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("adminDashboard.tenders.headers.run")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.headers.created")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.headers.extraction")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.headers.bidReview")}</TableHead>
                  <TableHead>{t("adminDashboard.tenders.headers.cvMatch")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailQuery.data.runs.map((run) => (
                  <TableRow
                    key={run.runId}
                    className="cursor-pointer"
                    onClick={() => setSelectedRunId(run.runId)}
                  >
                    <TableCell className="font-medium">#{run.runNumber}</TableCell>
                    <TableCell>{formatDateTime(run.createdAt, i18n.language)}</TableCell>
                    <TableCell>{run.extractionStatus ?? "—"}</TableCell>
                    <TableCell>{run.bidReviewStatus ?? "—"}</TableCell>
                    <TableCell>{run.cvMatchStatus ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <>
      <Card className="rounded-lg shadow-sm">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSearch className="size-5" />
              {t("adminDashboard.tenders.title")}
            </CardTitle>
            <p className="text-muted-foreground mt-2 text-sm">
              {t("adminDashboard.tenders.description")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(next) => {
                if (next === "runs" || next === "cases") {
                  setViewMode(next)
                }
              }}
            >
              <ToggleGroupItem value="runs">
                {t("adminDashboard.tenders.view.runs")}
              </ToggleGroupItem>
              <ToggleGroupItem value="cases">
                {t("adminDashboard.tenders.view.cases")}
              </ToggleGroupItem>
            </ToggleGroup>
            <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
            <Button size="sm" variant="outline" onClick={() => void handleRefresh()}>
              <RefreshCw className={`mr-2 size-4 ${isRefreshing ? "animate-spin" : ""}`} />
              {t("adminDashboard.tenders.actions.refresh")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <TenderUsageWindowStats query={usageSummaryQuery} />
          {mainTableContent}
        </CardContent>
      </Card>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>
              {detailQuery.data?.case.title ?? t("adminDashboard.tenders.detail.title")}
            </SheetTitle>
            <SheetDescription>{sheetDescription}</SheetDescription>
          </SheetHeader>
          {sheetBody}
        </SheetContent>
      </Sheet>
    </>
  )
}
